//#region IMPORT
import React, { useCallback, useEffect, useState } from 'react';
import MWorkPermitsBasic from './M_WorkPermits_BASIC';
import Global from '../../../../Services/Common/Global';
import { useDispatch, useSelector } from 'react-redux';
import { CommonActions } from '../../../../Reducer/CommonReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { TbmActions } from '../../../../Reducer/TbmReducer';
import { useBackStack } from '../../../../Hooks/back/useBackStack';
import { useTbmSessionSave } from '../hook/useSaveTbmSession';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { useSearch1, useSearch2 } from './M_WorkPermits_BASIC_Query';
import dayjs from 'dayjs';
import Commons from '../../../../Services/Common/Common';
import { PDFDocument } from 'pdf-lib';
import { usePdfStepTransition } from '../hook/usePdfStepTransition';
import { shouldOpenMeetingDrawerOnNext } from '../hook/tbmStepFlow';
import { IsTbmGuestMode, GetGuestPermitReport } from '../../GUEST/TbmGuestApi';
//#endregion

//#region 작업허가서 PDF 생성
/** TB_BS_CODE GROUP_CD=WORK_TYPE — CODE_NM → 레포트 필드명 (첨부 코드표와 동일) */
const WORK_TYPE_CODE_NM_TO_WT = {
  일반작업: 'WT_001',
  화기작업: 'WT_002',
  밀폐공간작업: 'WT_003',
  전기작업: 'WT_004',
  고소작업: 'WT_005',
  굴착작업: 'WT_006',
  잠수작업: 'WT_007',
};

/** TB_BS_CODE CODE — headerData workTypeCd{n} 등 */
const WORK_TYPE_WT_TO_CD = {
  WT_001: 'CD_WT_001',
  WT_002: 'CD_WT_002',
  WT_003: 'CD_WT_003',
  WT_004: 'CD_WT_004',
  WT_005: 'CD_WT_005',
  WT_006: 'CD_WT_006',
  WT_007: 'CD_WT_007',
};

/** GetPerNewR3 MST 행: camelCase / PascalCase / UPPER_SNAKE 키 허용 */
function PickDetail(row, ...keys) {
  if (row == null || typeof row !== 'object') return '';
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
    const v = row[k];
    if (v !== undefined && v !== null) return v;
  }
  return '';
}

function NormalizePerNewR3Payload(payload) {
  const p = payload && typeof payload === 'object' ? payload : {};

  const mst =
    (p.mst && typeof p.mst === 'object' && !Array.isArray(p.mst) && p.mst) ||
    (p.Mst && typeof p.Mst === 'object' && !Array.isArray(p.Mst) && p.Mst) ||
    (p.MST && typeof p.MST === 'object' && !Array.isArray(p.MST) && p.MST) ||
    null;

  const dailiesRaw =
    p.dailies ?? p.Dailies ?? p.DAILIES ?? p.dailyList ?? p.DailyList ?? [];
  const dailies = Array.isArray(dailiesRaw) ? dailiesRaw : [];

  const centerSignsRaw =
    p.centerSigns ??
    p.CenterSigns ??
    p.CENTER_SIGNS ??
    p.centerSignList ??
    p.CenterSignList ??
    [];
  const centerSigns = Array.isArray(centerSignsRaw) ? centerSignsRaw : [];

  return { mst, dailies, centerSigns };
}

function FlattenPerNewDetails(dailies) {
  const rows = [];
  const dailyArr = Array.isArray(dailies) ? dailies : [];
  for (const daily of dailyArr) {
    if (!daily || typeof daily !== 'object') continue;
    const workDate =
      daily.workDate ??
      daily.WorkDate ??
      daily.WORK_DATE ??
      daily.workYmd ??
      '';
    const dailyCd = daily.dailyCd ?? daily.DailyCd ?? daily.DAILY_CD ?? '';
    const detailsRaw =
      daily.details ??
      daily.Details ??
      daily.DETAILS ??
      daily.detailList ??
      daily.DetailList ??
      daily.permits ??
      daily.Permits ??
      [];
    const details = Array.isArray(detailsRaw) ? detailsRaw : [];
    for (const det of details) {
      if (!det || typeof det !== 'object') continue;
      rows.push({
        ...det,
        workDate,
        dailyCd,
      });
    }
  }
  return rows;
}

/** Detail의 WorkTypeNm 기준으로 해당 작업종류 행만 (종류별 1장 레포트용) */
function FilterPerNewDetailsByWorkTypeNm(detailRows, workTypeNm) {
  if (workTypeNm == null || String(workTypeNm).trim() === '') {
    return detailRows;
  }
  const target = String(workTypeNm).trim();
  return detailRows.filter((d) => String(d.workTypeNm ?? '').trim() === target);
}

/** detail에서 연장 안전관리자 성명·서명경로 — 슬라이스 내 첫 비어 있지 않은 값 */
function PickDetailExtSmFields(details) {
  let extSmNm = '';
  let extSmSignPath = '';
  for (const d of details) {
    const nm = d?.extSmNm ?? '';
    const path = d?.extSmSignPath ?? '';
    const nmT = String(nm ?? '').trim();
    const pathT = String(path ?? '').trim();
    if (!extSmNm && nmT) extSmNm = nmT;
    if (!extSmSignPath && pathT) extSmSignPath = pathT;
    if (extSmNm && extSmSignPath) break;
  }
  return { extSmNm, extSmSignPath };
}

/** detail 작업일시 종료: EndTime만 (연장 ExtEndTime 미사용) */
function PickDetailEndTimeForWork(d) {
  return d?.endTime ?? '';
}

/** detail 연장 종료: ExtEndTime만. 없으면 빈값(EndTime으로 채우지 않음) */
function PickDetailEndTimeForExtension(d) {
  const ext = d?.extEndTime ?? '';
  if (ext == null || String(ext).trim() === '') return '';
  return ext;
}

/** Detail StrTime/EndTime — HH:mm 정규화 (목록 그리드 padHm과 동일) */
function PadHmPerNewTime(t) {
  if (t == null || t === '') return '';
  const parts = String(t).split(':');
  const h = parts[0] ?? '00';
  const m = parts[1] ?? '00';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * 작업종류 슬라이스 detail에서 StrTime 최소,
 * 작업 종료(EndTime만) 최대, 연장 종료(ExtEndTime 있는 행만) 최대
 */
function AggregateDetailStrAndEndTimes(details) {
  let minStr = '';
  let maxEndWork = '';
  let maxEndExt = '';
  for (const d of details) {
    const st = d?.strTime;
    const enWork = PickDetailEndTimeForWork(d);
    const enExt = PickDetailEndTimeForExtension(d);
    const stP = PadHmPerNewTime(st) || String(st ?? '').trim();
    const enWorkP = PadHmPerNewTime(enWork) || String(enWork ?? '').trim();
    const enExtP = PadHmPerNewTime(enExt) || String(enExt ?? '').trim();
    if (stP) {
      if (!minStr || stP.localeCompare(minStr) < 0) minStr = stP;
    }
    if (enWorkP) {
      if (!maxEndWork || enWorkP.localeCompare(maxEndWork) > 0) {
        maxEndWork = enWorkP;
      }
    }
    if (enExtP) {
      if (!maxEndExt || enExtP.localeCompare(maxEndExt) > 0) {
        maxEndExt = enExtP;
      }
    }
  }
  return { strTime: minStr, endTime: maxEndWork, extEndTime: maxEndExt };
}

/** daily의 workDate + detail 시각 → 레포트용 `YYYY-MM-DD HH:mm` */
function FormatPerNewDetailYmdHm(dateRaw, hmRaw) {
  const hm = PadHmPerNewTime(hmRaw) || String(hmRaw ?? '').trim();
  if (dateRaw == null || dateRaw === '') {
    return hm || '';
  }
  const d = dayjs(dateRaw);
  const ymd = d.isValid() ? d.format('YYYY-MM-DD') : '';
  if (ymd && hm) return `${ymd} ${hm}`;
  if (ymd) return ymd;
  return hm || '';
}

/**
 * dailies(또는 dailiesByPmCd 병합) 기준 각 detail 행:
 * (workDate+strTime) ~ (workDate+endTime), 복수 행은 ", "로 이어 붙임.
 */
function BuildWorkDateTimeTextFromDetails(detailsSlice) {
  const segments = [];
  for (const d of detailsSlice) {
    const wd = d.workDate ?? '';
    const st = d.strTime ?? '';
    const en = PickDetailEndTimeForWork(d);
    const start = FormatPerNewDetailYmdHm(wd, st);
    const end = FormatPerNewDetailYmdHm(wd, en);
    if (start && end) segments.push(`${start} ~ ${end}`);
    else if (start) segments.push(start);
    else if (end) segments.push(end);
  }
  return segments.join(', ');
}

/** ExtEndTime 없는 행은 연장 구간에 포함하지 않음 */
function BuildExtensionDateTimeTextFromDetails(detailsSlice) {
  const segments = [];
  for (const d of detailsSlice) {
    const en = PickDetailEndTimeForExtension(d);
    if (!en || String(en).trim() === '') continue;
    const wd = d.workDate ?? '';
    const st = d.strTime ?? '';
    const start = FormatPerNewDetailYmdHm(wd, st);
    const end = FormatPerNewDetailYmdHm(wd, en);
    if (start && end) segments.push(`${start} ~ ${end}`);
    else if (end) segments.push(end);
  }
  return segments.join(', ');
}

function AsPerNewArray(v) {
  if (v == null) return [];
  return Array.isArray(v) ? v : [];
}

/** TB_BO_PERMIT_ITEM_* ItemCheck: 위험/장비 등 확인(0) 등 */
function PerNewItemCheckConfirmed(v) {
  return v === '0' || v === 0 || v === true;
}

function MergePerNewRiskItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const risks = AsPerNewArray(det?.riskItems ?? det?.RiskItems);
    for (const ri of risks) {
      const code = String(ri?.riskCode ?? '').trim();
      if (!code) continue;
      const name = ri?.riskNm ?? '';
      const checked = PerNewItemCheckConfirmed(ri?.itemCheck ?? ri?.ItemCheck);
      const prev = byCode.get(code);
      if (!prev) {
        byCode.set(code, { code, name, checked });
      } else {
        byCode.set(code, {
          code,
          name: prev.name || name,
          checked: prev.checked || checked,
        });
      }
    }
  }
  return [...byCode.values()].sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

function MergePerNewEquipItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const items = AsPerNewArray(det?.equipItems ?? det?.EquipItems);
    for (const it of items) {
      const code = String(it?.workEquipCode ?? '').trim();
      if (!code) continue;
      const name = it?.workEquipNm ?? '';
      const checked = PerNewItemCheckConfirmed(it?.itemCheck ?? it?.ItemCheck);
      const prev = byCode.get(code);
      if (!prev) {
        byCode.set(code, { code, name, checked });
      } else {
        byCode.set(code, {
          code,
          name: prev.name || name,
          checked: prev.checked || checked,
        });
      }
    }
  }
  return [...byCode.values()].sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

/** 준비사항 prepItems ItemCheck: '0'=필요(필요 열), '1'=필요 아님(확인 열) */
function PrepItemCheckIsNeed(v) {
  return v === '0' || v === 0;
}

function PrepItemCheckIsNotNeed(v) {
  return v === '1' || v === 1;
}

function MergePerNewPrepItems(details) {
  const byCode = new Map();
  for (const det of details) {
    const items = AsPerNewArray(det?.prepItems ?? det?.PrepItems);
    for (const it of items) {
      const code = String(it?.preparationCode ?? '').trim();
      if (!code) continue;
      const name = it?.preparationNm ?? '';
      const v = it?.itemCheck ?? it?.ItemCheck;
      const isNeed = PrepItemCheckIsNeed(v);
      const isNotNeed = PrepItemCheckIsNotNeed(v);
      const prev = byCode.get(code);
      const need = prev ? prev.need || isNeed : isNeed;
      const notNeed = prev ? prev.notNeed || isNotNeed : isNotNeed;
      byCode.set(code, {
        code,
        name: prev?.name || name,
        need,
        notNeed,
        /** 선택된 준비사항 목록 등 — 필요('0') 여부 */
        checked: need,
      });
    }
  }
  return [...byCode.values()].sort((a, b) =>
    String(a.code).localeCompare(String(b.code))
  );
}

const ConvertImageToBase64 = async (url) => {
  if (!url) return '';
  try {
    const response = await fetch(url);
    if (!response.ok) return '';
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result); // Base64 문자열 반환
      };
      reader.onerror = reject; // 오류 처리
      reader.readAsDataURL(blob); // Blob을 Base64로 변환
    });
  } catch (error) {
    console.error('이미지 변환 오류:', error);
    return '';
  }
};
//#endregion

function M_WorkPermits_BASICF(props) {
  //#region 변수
  const dispatch = useDispatch();
  const { sessionData, stepData, participantData, pageNumber } = useSelector(
    (state) => state.tbm
  );
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const activeStep = useSelector((state) => state.common.activeStep);

  const [loading, setLoading] = useState(false);
  const [fullUrl, setFullUrl] = useState();
  const [renderPreview, setRenderPreview] = useState(null);
  const [numPages, setNumPages] = useState();
  const [pageWidth, setPageWidth] = useState(window.innerWidth * 1);
  const [pageViewHeight, setPageViewHeight] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false); // pageNumber 초기화 상태 확인을 위한 state
  const [transitionDirection, setTransitionDirection] = useState('next');

  const blobUrlRef = React.useRef(null);
  const isMountedRef = React.useRef(true);

  const CleanupPermitPreview = useCallback(() => {
    if (blobUrlRef.current) {
      try {
        URL.revokeObjectURL(blobUrlRef.current);
      } catch (_) {}
      blobUrlRef.current = null;
    }
    setFullUrl(null);
    setRenderPreview(null);
    setNumPages(undefined);
    setLoading(false);
  }, []);

  const { isTransitioning, startTransition } = usePdfStepTransition({
    cleanup: CleanupPermitPreview,
    isMountedRef,
  });
  //#endregion

  //#region 커스텀훅
  useBackStack({
    type: 'custom',
    enabled: activeStep > 0,
    onBack: () => {
      if (participantData.hostYn !== 'Y') {
        dispatch(
          showSnackbar({
            message: 'TBM 진행중 뒤로가기가 불가능합니다.',
            type: 'warning',
          })
        );
        return;
      }
      setTransitionDirection('prev');
      props.ActiveButton({ type: 'stepChange', step: activeStep - 1 });
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: true,
          direction: 'prev',
        })
      );
      startTransition(() => {
        dispatch(
          TbmActions.setStepTransition({
            isTransitioning: false,
            direction: 'prev',
          })
        );
        dispatch(CommonActions.setActiveStep(activeStep - 1));
      });
    },
  });
  const { SaveTbmSession } = useTbmSessionSave();
  //#endregion

  //#region 이펙트
  const Init = async () => {
    try {
      const lastPosition = Number(
        stepData.find((f) => f.stepCode === props.stepCode)?.lastPosition
      );
      const initialPage = lastPosition > 0 ? lastPosition : 1;
      dispatch(TbmActions.setPageNumber(initialPage));
      await Search1Async({ depCd: sessionData.depCd });
    } catch (e) {
      console.log(e);
      setFullUrl(null);
      setRenderPreview(null);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    Init();
    const handleResize = () => {
      setPageWidth(window.innerWidth * 1);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleResize);
      if (blobUrlRef.current) {
        try {
          URL.revokeObjectURL(blobUrlRef.current);
        } catch (_) {}
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const lastPosition = Number(
      stepData.find((f) => f.stepCode === props.stepCode)?.lastPosition ?? 0
    );
    if (!isInitialized) {
      if (
        pageNumber === lastPosition ||
        (lastPosition === 0 && pageNumber === 1)
      ) {
        setIsInitialized(true);
      } else {
        if (loading) setLoading(false);
        return;
      }
    }
    if (loading) setLoading(false);
    if (Object.keys(sessionData).length === 0) return;
    if (participantData.hostYn === 'Y') {
      const currentStep = stepData.find((f) => f.stepCode === props.stepCode);
      SaveTbmSession([
        {
          ...sessionData,
          curStepOrder: currentStep?.stepOrder,
          curStepCode: currentStep?.stepCode,
          curPosition: lastPosition === 0 ? 1 : pageNumber,
          curContentOrder: currentStep?.curContentOrder ?? 1,
        },
      ]);
    }
  }, [pageNumber]);

  /** PDF 총 페이지 확정 후 pageNumber가 [1, numPages] 밖이면 보정(stepData·세션 불일치 대비) */
  useEffect(() => {
    if (!numPages || numPages < 1) return;
    const safe = Math.min(Math.max(1, pageNumber), numPages);
    if (safe === pageNumber) return;
    dispatch(TbmActions.setPageNumber(safe));
    if (participantData.hostYn === 'Y') {
      props.ActiveButton({
        type: 'pageChange',
        pageNumber: safe,
      });
    }
  }, [numPages, pageNumber]);
  //#endregion

  //#region 쿼리
  const { runAsync: Search1Async } = useSearch1({
    onSuccess: async (rows) => {
      setLoading(true);
      try {
        const pmCds = rows
          .filter((r) => r.smSign === 'Y' && r.depSign === 'Y')
          .map((r) => String(r.pmCd))
          .filter(Boolean);

        if (pmCds.length === 0) {
          setFullUrl(null);
          setRenderPreview(null);
          return;
        }

        const permitBlobs = [];

        const executePermitPdfForPayload = async (payload) => {
          const env = NormalizePerNewR3Payload(payload);
          const rowMst =
            env.mst && typeof env.mst === 'object' && !Array.isArray(env.mst)
              ? env.mst
              : null;

          if (!rowMst) {
            return null;
          }

          const P = (...keys) => PickDetail(rowMst, ...keys);

          const reportData = {
            pmCd: P('pmCd', 'PmCd', 'PM_CD'),
            custCd: P('custCd', 'CustCd', 'CUST_CD'),
            depCd: P('depCd', 'DepCd', 'DEP_CD'),
            reqId: P('reqId', 'ReqId', 'REQ_ID'),
            reqNm: P('reqNm', 'ReqNm', 'REQ_NM'),
            reqDepNm: P('reqDepNm', 'ReqDepNm', 'REQ_DEP_NM'),
            reqRank: P('reqRank', 'ReqRank', 'REQ_RANK'),
            reqPosition: P('reqPosition', 'ReqPosition', 'REQ_POSITION'),
            reqSignPath: P('reqSignPath', 'ReqSignPath', 'REQ_SIGN_PATH'),
            workItems: P('workItems', 'WorkItems', 'WORK_ITEMS'),
            totalStartDate: P(
              'totalStartDate',
              'TotalStartDate',
              'TOTAL_START_DATE'
            ),
            totalEndDate: P('totalEndDate', 'TotalEndDate', 'TOTAL_END_DATE'),
            workLoc: P('workLoc', 'WorkLoc', 'WORK_LOC'),
            workContent: P('workContent', 'WorkContent', 'WORK_CONTENT'),
            workMemberNo: P('workMemberNo', 'WorkMemberNo', 'WORK_MEMBER_NO'),
            partnerNm: P('partnerNm', 'PartnerNm', 'PARTNER_NM'),
            partnerManager: P(
              'partnerManager',
              'PartnerManager',
              'PARTNER_MANAGER'
            ),
            partnerTel: P('partnerTel', 'PartnerTel', 'PARTNER_TEL'),
            workerList: P('workerList', 'WorkerList', 'WORKER_LIST'),
            depSign: P('depSign', 'DepSign', 'DEP_SIGN'),
            smSign: P('smSign', 'SmSign', 'SM_SIGN'),
            smId: P('smId', 'SmId', 'SM_ID'),
            smNm: P('smNm', 'SmNm', 'SM_NM'),
            smDepNm: P('smDepNm', 'SmDepNm', 'SM_DEP_NM'),
            smRank: P('smRank', 'SmRank', 'SM_RANK'),
            smPosition: P('smPosition', 'SmPosition', 'SM_POSITION'),
            smSignPath: P('smSignPath', 'SmSignPath', 'SM_SIGN_PATH'),
            smSignDate: P('smSignDate', 'SmSignDate', 'SM_SIGN_DATE'),
            extSmNm: P('extSmNm', 'ExtSmNm', 'EXT_SM_NM'),
            extSmSignPath: P(
              'extSmSignPath',
              'ExtSmSignPath',
              'EXT_SM_SIGN_PATH'
            ),
            returnYn: P('returnYn', 'ReturnYn', 'RETURN_YN'),
            returnDetail: P('returnDetail', 'ReturnDetail', 'RETURN_DETAIL'),
            returnId: P('returnId', 'ReturnId', 'RETURN_ID'),
            witnessId: P('witnessId', 'WitnessId', 'WITNESS_ID'),
            witnessYn: P('witnessYn', 'WitnessYn', 'WITNESS_YN'),
            witnessNm: P('witnessNm', 'WitnessNm', 'WITNESS_NM'),
            witnessSignPath: P(
              'witnessSignPath',
              'WitnessSignPath',
              'WITNESS_SIGN_PATH'
            ),
            witnessDepNm: P('witnessDepNm', 'WitnessDepNm', 'WITNESS_DEP_NM'),
            witnessRank: P('witnessRank', 'WitnessRank', 'WITNESS_RANK'),
            witnessPosition: P(
              'witnessPosition',
              'WitnessPosition',
              'WITNESS_POSITION'
            ),
            checkRemark: P('checkRemark', 'CheckRemark', 'CHECK_REMARK'),
            status: P('status', 'Status', 'STATUS'),
            createId: P('createId', 'CreateId', 'CREATE_ID'),
            createDate: P('createDate', 'CreateDate', 'CREATE_DATE'),
            updateId: P('updateId', 'UpdateId', 'UPDATE_ID'),
            updateDate: P('updateDate', 'UpdateDate', 'UPDATE_DATE'),
          };

          // 예외적으로 MST에 camelCase/ PascalCase 혼재로 추가 필드가 올 수 있어 보완
          for (const [k, v] of Object.entries(rowMst)) {
            if (v === undefined || v === null) continue;
            if (
              /^[a-z][a-zA-Z0-9]*$/.test(k) &&
              !Object.prototype.hasOwnProperty.call(reportData, k)
            ) {
              reportData[k] = v;
              continue;
            }
            if (/^[A-Z][a-z]/.test(k)) {
              const camel = k.charAt(0).toLowerCase() + k.slice(1);
              if (!Object.prototype.hasOwnProperty.call(reportData, camel)) {
                reportData[camel] = v;
              }
            }
          }

          const detailRows = FlattenPerNewDetails(env.dailies);

          const executePermitPdf = async (detailPoolRows, options = {}) => {
            const { mode = 'full', selectedRows = [] } = options;

            const formatDateYmd = (dateValue) => {
              if (!dateValue) return '';
              const formatted = dayjs(dateValue).format('YYYY-MM-DD');
              return formatted === 'Invalid Date' ? '' : formatted;
            };

            let smSignBase64 = null;
            const smPath = String(reportData.smSignPath || '').trim();
            if (smPath) {
              const safetyImageUrl =
                Global.file.useUrl + 'files/' + Commons.EncodeUrl(smPath);
              const base64Image = await ConvertImageToBase64(safetyImageUrl);
              smSignBase64 = String(base64Image || '').replace(
                /^data:image\/[a-z]+;base64,/,
                ''
              );
            }

            let reqSignBase64 = null;
            const reqSignPathVal = String(
              reportData.reqSignPath ?? reportData.ReqSignPath ?? ''
            ).trim();
            if (reqSignPathVal) {
              try {
                const reqSignUrl =
                  Global.file.useUrl +
                  'files/' +
                  Commons.EncodeUrl(reqSignPathVal);
                const reqB64 = await ConvertImageToBase64(reqSignUrl);
                reqSignBase64 = String(reqB64 || '').replace(
                  /^data:image\/[a-z]+;base64,/,
                  ''
                );
              } catch {
                reqSignBase64 = null;
              }
            }

            let witnessSignBase64 = null;
            const witnessSignPathVal = String(
              reportData.witnessSignPath ?? reportData.WitnessSignPath ?? ''
            ).trim();
            if (witnessSignPathVal) {
              try {
                const witnessSignUrl =
                  Global.file.useUrl +
                  'files/' +
                  Commons.EncodeUrl(witnessSignPathVal);
                const witnessB64 = await ConvertImageToBase64(witnessSignUrl);
                witnessSignBase64 = String(witnessB64 || '').replace(
                  /^data:image\/[a-z]+;base64,/,
                  ''
                );
              } catch {
                witnessSignBase64 = null;
              }
            }

            const createDate = reportData.createDate || '';
            const permitDate = createDate
              ? dayjs(createDate).format('YYYY-MM-DD')
              : '';

            const SplitCommaTrim = (v) =>
              String(v ?? '')
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0);

            const NormalizeWorkTypeToken = (token) => {
              const t = String(token ?? '').trim();
              if (!t) return '';
              return t.replace(/^CD_/i, '');
            };

            const workItemNames = SplitCommaTrim(reportData.workItems);
            const workTypeCodeTokens = SplitCommaTrim(
              reportData.workTypeCds || reportData.workTypes || ''
            ).map(NormalizeWorkTypeToken);

            const emptyWtFlags = {
              WT_001: false,
              WT_002: false,
              WT_003: false,
              WT_004: false,
              WT_005: false,
              WT_006: false,
              WT_007: false,
            };

            const ResolveWorkTypeWtKey = (nm, i) => {
              const code = workTypeCodeTokens[i];
              const fromToken =
                code && Object.prototype.hasOwnProperty.call(emptyWtFlags, code)
                  ? code
                  : '';
              const fromNm = WORK_TYPE_CODE_NM_TO_WT[nm] || '';
              return fromToken || fromNm;
            };

            const workTypeNameJoined =
              workItemNames.length > 0
                ? workItemNames.join(' · ')
                : String(reportData.workItems ?? '')
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .join(' · ');

            const centerTableRows = [];
            for (const cs of env.centerSigns) {
              const path = String(
                cs.centerSignPath ?? cs.CenterSignPath ?? ''
              ).trim();
              let centerB64 = null;
              if (path) {
                try {
                  const u =
                    Global.file.useUrl + 'files/' + Commons.EncodeUrl(path);
                  const rawB64 = await ConvertImageToBase64(u);
                  centerB64 = String(rawB64 || '').replace(
                    /^data:image\/[a-z]+;base64,/,
                    ''
                  );
                } catch {
                  centerB64 = null;
                }
              }
              centerTableRows.push({
                CENTER_ID: cs.centerId ?? cs.CenterId ?? '',
                CENTER_NAME: cs.centerName ?? cs.CenterName ?? '',
                CENTER_RANK: cs.centerRank ?? cs.CenterRank ?? '',
                CENTER_POSITION: cs.centerPosition ?? cs.CenterPosition ?? '',
                CENTER_DEP_NM: cs.centerDepNm ?? cs.CenterDepNm ?? '',
                CENTER_SIGN_IMAGE: centerB64,
                CENTER_UPDATE_YMD: formatDateYmd(
                  cs.updateDate ?? cs.UpdateDate
                ),
              });
            }

            let slices;
            if (mode === 'selection') {
              slices = (selectedRows || []).map((r, idx) => ({
                key: `row-${idx}`,
                dtlCd: String(r?.dtlCd ?? r?.DtlCd ?? '').trim(),
                workTypeNm: String(r?.workTypeNm ?? r?.WorkTypeNm ?? '').trim(),
              }));
              if (slices.length === 0) return null;
            } else {
              slices = workItemNames.length > 0 ? workItemNames : [''];
            }

            const GetSliceKey = (slice) =>
              typeof slice === 'string'
                ? String(slice)
                : String(slice?.key ?? slice?.dtlCd ?? slice?.workTypeNm ?? '');
            const GetSliceNm = (slice) =>
              typeof slice === 'string'
                ? String(slice)
                : String(slice?.workTypeNm ?? '');
            const GetSliceDetails = (slice) => {
              if (mode !== 'selection' || typeof slice === 'string') {
                return FilterPerNewDetailsByWorkTypeNm(detailPoolRows, slice);
              }
              const dtlCd = String(slice?.dtlCd ?? '').trim();
              if (dtlCd) {
                return detailPoolRows.filter(
                  (d) => String(d.dtlCd ?? d.DtlCd ?? '').trim() === dtlCd
                );
              }
              return FilterPerNewDetailsByWorkTypeNm(
                detailPoolRows,
                slice?.workTypeNm
              );
            };

            const mstStartDateYmd = reportData.totalStartDate
              ? formatDateYmd(reportData.totalStartDate)
              : '';
            const mstEndDateYmd = reportData.totalEndDate
              ? formatDateYmd(reportData.totalEndDate)
              : '';

            const extSmSignImageBySlice = {};
            for (const slice of slices) {
              const sliceKey = GetSliceKey(slice);
              const sliceDetailsForExt = GetSliceDetails(slice);
              let extPath =
                PickDetailExtSmFields(sliceDetailsForExt).extSmSignPath;
              extPath = String(extPath ?? '').trim();
              if (!extPath) {
                extPath = String(
                  reportData.extSmSignPath ?? reportData.ExtSmSignPath ?? ''
                ).trim();
              }
              if (extPath) {
                try {
                  const extUrl =
                    Global.file.useUrl + 'files/' + Commons.EncodeUrl(extPath);
                  const rawB64 = await ConvertImageToBase64(extUrl);
                  extSmSignImageBySlice[sliceKey] = String(
                    rawB64 || ''
                  ).replace(/^data:image\/[a-z]+;base64,/, '');
                } catch {
                  extSmSignImageBySlice[sliceKey] = null;
                }
              } else {
                extSmSignImageBySlice[sliceKey] = null;
              }
            }

            const BuildPrintDataForWorkTypeSlice = (slice) => {
              const sliceNm = GetSliceNm(slice);
              const sliceKey = GetSliceKey(slice);
              const detailsSlice = GetSliceDetails(slice);
              const { extSmNm: detExtSmNm, extSmSignPath: detExtSmSignPath } =
                PickDetailExtSmFields(detailsSlice);
              const {
                strTime: sliceStrTime,
                endTime: sliceEndTime,
                extEndTime: sliceExtEndTime,
              } = AggregateDetailStrAndEndTimes(detailsSlice);
              const workDateTimeFromDetails =
                BuildWorkDateTimeTextFromDetails(detailsSlice);
              const extensionDateTimeFromDetails =
                BuildExtensionDateTimeTextFromDetails(detailsSlice);
              const workDateTimeText =
                workDateTimeFromDetails ||
                [
                  [mstStartDateYmd, sliceStrTime].filter(Boolean).join(' '),
                  [mstEndDateYmd, sliceEndTime].filter(Boolean).join(' '),
                ]
                  .filter((s) => s.length > 0)
                  .join(' ~ ');
              const extensionDateTimeText =
                extensionDateTimeFromDetails ||
                (String(sliceExtEndTime ?? '').trim()
                  ? [
                      [mstStartDateYmd, sliceStrTime].filter(Boolean).join(' '),
                      [mstEndDateYmd, sliceExtEndTime]
                        .filter(Boolean)
                        .join(' '),
                    ]
                      .filter((s) => s.length > 0)
                      .join(' ~ ')
                  : '');

              const riskMergedPage = MergePerNewRiskItems(detailsSlice);
              const equipMergedPage = MergePerNewEquipItems(detailsSlice);
              const prepMergedPage = MergePerNewPrepItems(detailsSlice);

              const riskRowData = {};
              riskMergedPage.forEach((item, index) => {
                const fieldIndex = index + 1;
                riskRowData[`RISK_${fieldIndex}_CHECKED`] = item.checked;
                riskRowData[`RISK_${fieldIndex}_NAME`] = item.name || '';
                riskRowData[`RISK_${fieldIndex}_CODE`] = item.code || '';
              });
              const riskTableDataHorizontal = [riskRowData];

              const equipmentRowData = {};
              equipMergedPage.forEach((item, index) => {
                const fieldIndex = index + 1;
                equipmentRowData[`EQUIP_${fieldIndex}_CHECKED`] = item.checked;
                equipmentRowData[`EQUIP_${fieldIndex}_NAME`] = item.name || '';
                equipmentRowData[`EQUIP_${fieldIndex}_CODE`] = item.code || '';
              });
              const equipmentTableDataHorizontal = [equipmentRowData];

              const validPreparationItems = prepMergedPage.filter(
                (item) => item && item.code && item.name
              );
              const preparationTableData = validPreparationItems.map(
                (prepItem) => ({
                  PREP_CODE: prepItem.code || '',
                  PREP_NAME: prepItem.name || '',
                  PREP_CHECKED: !!prepItem.need,
                  WORKER_NAME: '',
                })
              );

              const titleNm = sliceNm || workTypeNameJoined;
              const idxInList = sliceNm
                ? workItemNames.findIndex((n) => n === sliceNm)
                : -1;
              const wtKey =
                idxInList >= 0
                  ? ResolveWorkTypeWtKey(sliceNm, idxInList)
                  : sliceNm
                    ? WORK_TYPE_CODE_NM_TO_WT[sliceNm] || ''
                    : '';

              const pageWtFlags = { ...emptyWtFlags };
              if (
                wtKey &&
                Object.prototype.hasOwnProperty.call(pageWtFlags, wtKey)
              ) {
                pageWtFlags[wtKey] = true;
              }

              const TableDataMain = [
                {
                  ...emptyWtFlags,
                  WORK_ITEM_NM: sliceNm,
                  WORK_TYPE_NM: sliceNm,
                  LINE_NO: 1,
                  WORK_DATE_TIME: workDateTimeText,
                  WorkDateTime: workDateTimeText,
                  EXTENSION_DATE_TIME: extensionDateTimeText,
                  ExtensionDateTime: extensionDateTimeText,
                  ...(wtKey ? { [wtKey]: true } : {}),
                },
              ];

              const selectedRiskNames = riskMergedPage
                .filter((x) => x.checked)
                .map((x) => x.name)
                .filter(Boolean)
                .join(', ');
              const selectedEquipmentNames = equipMergedPage
                .filter((x) => x.checked)
                .map((x) => x.name)
                .filter(Boolean)
                .join(', ');
              const selectedPreparationNames = prepMergedPage
                .filter((x) => x.checked)
                .map((x) => x.name)
                .filter(Boolean)
                .join(', ');
              const etcRemarkPage = detailsSlice
                .map((d) => d.etcRemark ?? d.EtcRemark ?? '')
                .filter(Boolean)
                .join(' / ');
              const checkRemarkPage = detailsSlice
                .map(
                  (d) => d.checkRemark ?? d.CheckRemark ?? d.CHECK_REMARK ?? ''
                )
                .filter(Boolean)
                .join(' / ');

              /** 상세(detail) 행의 입회자 — MST witnessNm·witnessDepNm 등과 별도 */
              const pickDetailWitnessField = (d, keys) => {
                for (const k of keys) {
                  const v = d[k];
                  if (v != null && String(v).trim() !== '')
                    return String(v).trim();
                }
                return '';
              };
              const mergeDetailWitnessUnique = (keys) =>
                [
                  ...new Set(
                    detailsSlice
                      .map((d) => pickDetailWitnessField(d, keys))
                      .filter(Boolean)
                  ),
                ].join(' / ');
              const workTypeWitnessNm = mergeDetailWitnessUnique(['witnessNm']);
              const workTypeWitnessRank = mergeDetailWitnessUnique([
                'witnessRank',
              ]);
              const workTypeWitnessPosition = mergeDetailWitnessUnique([
                'witnessPosition',
              ]);
              const workTypeWitnessDepNm = mergeDetailWitnessUnique([
                'witnessDepNm',
              ]);
              const workTypeWitnessSignPath = mergeDetailWitnessUnique([
                'witnessSignPath',
                'WitnessSignPath',
                'WITNESS_SIGN_PATH',
              ]);

              const headerData = {
                pmCd: reportData.pmCd ?? '',
                custCd: reportData.custCd ?? '',
                depCd: reportData.depCd ?? '',
                reqId: reportData.reqId ?? '',
                reqNm: reportData.reqNm ?? '',
                reqDepNm: reportData.reqDepNm ?? '',
                reqRank: reportData.reqRank ?? '',
                reqPosition: reportData.reqPosition ?? '',
                reqSignPath: reportData.reqSignPath ?? '',
                ReqSignPath:
                  reportData.reqSignPath ?? reportData.ReqSignPath ?? '',
                workItems: sliceNm || reportData.workItems || '',
                WORK_TYPE_NAME: titleNm,
                workTypeName: titleNm,
                totalStartDate: mstStartDateYmd,
                totalEndDate: mstEndDateYmd,
                strTime: sliceStrTime,
                steTime: sliceStrTime,
                endTime: sliceEndTime,
                extEndTime: sliceExtEndTime,
                workDateTimeText,
                extensionDateTimeText,
                WORK_DATE_TIME: workDateTimeText,
                WorkDateTime: workDateTimeText,
                EXTENSION_DATE_TIME: extensionDateTimeText,
                ExtensionDateTime: extensionDateTimeText,
                extSmNm:
                  String(detExtSmNm ?? '').trim() ||
                  String(reportData.extSmNm ?? '').trim() ||
                  '',
                extSmSignPath:
                  String(detExtSmSignPath ?? '').trim() ||
                  String(reportData.extSmSignPath ?? '').trim() ||
                  '',
                workLoc: reportData.workLoc ?? '',
                workContent: reportData.workContent ?? '',
                workMemberNo: reportData.workMemberNo ?? '',
                partnerNm: reportData.partnerNm ?? '',
                partnerManager: reportData.partnerManager ?? '',
                partnerMan: reportData.partnerManager ?? '',
                partnerTel: reportData.partnerTel ?? '',
                workerList: reportData.workerList ?? '',
                depSign: reportData.depSign ?? '',
                smSign: reportData.smSign ?? '',
                smId: reportData.smId ?? '',
                smNm: reportData.smNm ?? '',
                smDepNm: reportData.smDepNm ?? '',
                smRank: reportData.smRank ?? '',
                smPosition: reportData.smPosition ?? '',
                smSignPath: reportData.smSignPath ?? '',
                smSignDate: reportData.smSignDate ?? '',
                returnYn: reportData.returnYn ?? '',
                returnDetail: reportData.returnDetail ?? '',
                returnId: reportData.returnId ?? '',
                witnessId: reportData.witnessId ?? '',
                witnessYn: reportData.witnessYn ?? '',
                witnessNm:
                  reportData.witnessNm || workTypeWitnessNm || '',
                witnessSignPath: reportData.witnessSignPath ?? '',
                WitnessSignPath:
                  reportData.witnessSignPath ??
                  reportData.WitnessSignPath ??
                  '',
                witnessDepNm:
                  reportData.witnessDepNm || workTypeWitnessDepNm || '',
                witnessRank:
                  reportData.witnessRank || workTypeWitnessRank || '',
                witnessPosition:
                  reportData.witnessPosition || workTypeWitnessPosition || '',
                workTypeWitnessNm,
                workTypeWitnessRank,
                workTypeWitnessPosition,
                workTypeWitnessDepNm,
                workTypeWitnessSignPath,
                checkRemark: checkRemarkPage || reportData.checkRemark || '',
                status: reportData.status ?? '',
                createId: reportData.createId ?? '',
                createDate: reportData.createDate ?? '',
                updateId: reportData.updateId ?? '',
                updateDate: reportData.updateDate ?? '',

                permitDate,
                workItemCount: sliceNm ? 1 : workItemNames.length,
                ...pageWtFlags,

                REQ_SIGN_IMAGE: reqSignBase64 || null,
                SM_SIGN_IMAGE: smSignBase64 || null,
                EXT_SM_SIGN_IMAGE: extSmSignImageBySlice[sliceKey] || null,
                WITNESS_SIGN_IMAGE: witnessSignBase64 || null,

                smSignDateYmd: formatDateYmd(reportData.smSignDate),
                selectedRiskNames,
                selectedEquipmentNames,
                selectedPreparationNames,
                etcRemark: etcRemarkPage,
                detailRowCount: detailsSlice.length,
                dailyCount: env.dailies.length,
                centerSignCount: env.centerSigns.length,
              };

              if (sliceNm) {
                headerData.workItemNm1 = sliceNm;
                headerData.workTypeNm1 = sliceNm;
                if (wtKey) {
                  headerData.workTypeWt1 = wtKey;
                  headerData.workTypeCd1 = WORK_TYPE_WT_TO_CD[wtKey] || '';
                }
              } else {
                workItemNames.forEach((nm, i) => {
                  const idx = i + 1;
                  const wk = ResolveWorkTypeWtKey(nm, i);
                  headerData[`workItemNm${idx}`] = nm;
                  headerData[`workTypeNm${idx}`] = nm;
                  if (wk) {
                    headerData[`workTypeWt${idx}`] = wk;
                    headerData[`workTypeCd${idx}`] =
                      WORK_TYPE_WT_TO_CD[wk] || '';
                  }
                });
              }

              return {
                FileName: 'PER_NEW',
                HeaderData: JSON.stringify(headerData),
                TableDataMain,
                TableData1: JSON.stringify(riskTableDataHorizontal),
                TableData2: JSON.stringify(equipmentTableDataHorizontal),
                TableData3: JSON.stringify(preparationTableData),
                TableData4: JSON.stringify(centerTableRows),
                TableData5: JSON.stringify([]),
                TableData6: JSON.stringify([]),
              };
            };

            const pdfBlobs = [];
            for (const slice of slices) {
              const printData = BuildPrintDataForWorkTypeSlice(slice);
              // 게스트는 회원 토큰이 없어 게스트용 PDF API(작업허가서 양식 고정) 사용
              const response = IsTbmGuestMode()
                ? await GetGuestPermitReport(printData)
                : await Commons.Axios(
                    'report',
                    Global.base.useUrl + Global.urls.Reports.reports,
                    printData
                  );
              pdfBlobs.push(response.data);
            }

            if (pdfBlobs.length === 0) return null;

            let finalBlob = pdfBlobs[0];
            if (pdfBlobs.length > 1) {
              const mergedPdf = await PDFDocument.create();
              for (const b of pdfBlobs) {
                const pdf = await PDFDocument.load(await b.arrayBuffer());
                const copied = await mergedPdf.copyPages(
                  pdf,
                  pdf.getPageIndices()
                );
                copied.forEach((p) => mergedPdf.addPage(p));
              }
              const mergedBytes = await mergedPdf.save();
              finalBlob = new Blob([mergedBytes], { type: 'application/pdf' });
            }

            return finalBlob;
          };

          return await executePermitPdf(detailRows, { mode: 'full' });
        };

        for (const pmCd of pmCds) {
          const detailsPayloadRows = await Search2Async({ pmCd, userInfo });
          const payload = detailsPayloadRows[0] ?? null;
          const permitBlob = payload
            ? await executePermitPdfForPayload(payload)
            : null;
          if (permitBlob) {
            permitBlobs.push(permitBlob);
          }
        }

        if (permitBlobs.length === 0) {
          dispatch(
            showSnackbar({
              message: '작업허가서 PDF를 생성할 데이터가 없습니다.',
              type: 'warning',
            })
          );
          setFullUrl(null);
          setRenderPreview(null);
          return;
        }

        let finalPermitBlob = permitBlobs[0];
        if (permitBlobs.length > 1) {
          const mergedPdf = await PDFDocument.create();
          for (const b of permitBlobs) {
            const pdf = await PDFDocument.load(await b.arrayBuffer());
            const copied = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copied.forEach((p) => mergedPdf.addPage(p));
          }
          const mergedBytes = await mergedPdf.save();
          finalPermitBlob = new Blob([mergedBytes], {
            type: 'application/pdf',
          });
        }

        // 기존 Blob URL 해제 후 새 URL 생성
        if (blobUrlRef.current) {
          try {
            URL.revokeObjectURL(blobUrlRef.current);
          } catch (_) {}
        }
        const blobUrl = URL.createObjectURL(finalPermitBlob);
        blobUrlRef.current = blobUrl;
        setFullUrl(blobUrl);
        // Base64 변환 제거 → Blob URL 직접 사용 (메모리 33% 절약)
        setRenderPreview(blobUrl);
      } catch (e) {
        console.log(e);
        setFullUrl(null);
        setRenderPreview(null);
      } finally {
        setLoading(false);
      }
    },
  });
  const { runAsync: Search2Async } = useSearch2();
  //#endregion

  //#region 나머지 함수
  const getExtensionFromUrl = (url = '') => {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      return 'pdf';
    }
    const parts = url?.split('.');
    return parts?.length > 1 ? parts.pop().toLowerCase() : '';
  };
  const onDocumentLoadSuccess = ({ numPages: total }) => setNumPages(total);

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setLoading(true);
      const prev = pageNumber - 1;
      dispatch(TbmActions.setPageNumber(prev));
      props.ActiveButton({
        type: 'pageChange',
        pageNumber: prev,
      });
    } else {
      setTransitionDirection('prev');
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: true,
          direction: 'prev',
        })
      );
      startTransition(() => {
        dispatch(
          TbmActions.setStepTransition({
            isTransitioning: false,
            direction: 'prev',
          })
        );
        dispatch(CommonActions.setActiveStep(Math.max(0, activeStep - 1)));
      });
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setLoading(true);
      const next = pageNumber + 1;
      dispatch(TbmActions.setPageNumber(next));
      props.ActiveButton({
        type: 'pageChange',
        pageNumber: next,
      });
    }
  };

  const HandleDocumentError = () => {
    dispatch(
      showSnackbar({
        message: '작업허가서를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.',
        type: 'error',
      })
    );
  };

  const advanceToNextStep = useCallback(() => {
    if (shouldOpenMeetingDrawerOnNext(stepData, props.stepCode)) {
      // 웹소켓 왕복(echo)에 의존하지 않고 즉시 로컬에서 팝업을 띄움
      dispatch(BottomSheetActions.OpenBottomSheet({ type: 'meetingDrawer' }));
      props.ActiveButton({ type: 'openMeetingDrawer' });
      return;
    }
    setTransitionDirection('next');
    props.ActiveButton({ type: 'stepChange', step: activeStep + 1 });
    dispatch(
      TbmActions.setStepTransition({ isTransitioning: true, direction: 'next' })
    );
    startTransition(() => {
      dispatch(
        TbmActions.setStepTransition({
          isTransitioning: false,
          direction: 'next',
        })
      );
      dispatch(CommonActions.setActiveStep(activeStep + 1));
    });
  }, [
    stepData,
    props.stepCode,
    startTransition,
    props.ActiveButton,
    activeStep,
  ]);
  //#endregion

  return (
    <MWorkPermitsBasic
      userInfo={userInfo}
      loading={loading}
      setLoading={setLoading}
      onDocumentLoadSuccess={onDocumentLoadSuccess}
      pageWidth={pageWidth}
      numPages={numPages}
      pageViewHeight={pageViewHeight}
      setPageViewHeight={setPageViewHeight}
      renderPreview={renderPreview}
      goToPrevPage={goToPrevPage}
      goToNextPage={goToNextPage}
      ActiveButton={props.ActiveButton}
      fullUrl={fullUrl}
      getExtensionFromUrl={getExtensionFromUrl}
      onDocumentError={HandleDocumentError}
      isTransitioning={isTransitioning}
      transitionDirection={transitionDirection}
      onAdvanceToNextStep={advanceToNextStep}
    />
  );
}

export default M_WorkPermits_BASICF;
